import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { fetchProgramList, fetchProgramImageById } from '../redux/actions/Actions'
import RequestCallback from '../RequestCallback'
import axiosinstance from '../../api/axiosinstance'
import JourneyWorkflow from './JourneyWorkflow'
import TechStackSection from './TechStackSection'
import SEO from '../common/SEO'
import IncludedCoursesSection from './IncludedCoursesSection'
import ProgramDurationSection from './ProgramDurationSection'
import WhyChooseSection from './WhyChooseSection'
import ProgramHighlightsSection from './ProgramHighlightsSection'
import LiveProjectsSection from './LiveProjectsSection'
import { MonitorIcon, CodeIcon, TargetIcon, ChatIcon, UsersIcon, AwardIcon, BriefcaseIcon } from './programHighlightIcons'
import { FaJava, FaPython, FaReact, FaAngular, FaNodeJs, FaAws, FaDocker, FaShieldAlt, FaMicrochip, FaMobileAlt, FaFigma, FaGitAlt } from 'react-icons/fa'
import { SiJavascript, SiExpress, SiMongodb, SiMysql, SiTensorflow, SiOpenai, SiMicrosoftazure, SiKubernetes, SiBlockchaindotcom } from 'react-icons/si'

const CB_LIVE_FEATURES = [
  'Work in real-time projects',
  'Company-like Environment',
  'Agile Methodology',
  'Code Reviews by Senior Developers',
  'QA Testing & Bug Fixes',
  'Client Interaction & Demos',
  'Deployment on Server',
];

const CB_LIVE_PROJECTS = [
  { name:'E-Commerce Platform',        icon:'🛒' },
  { name:'Hospital Management System', icon:'🏥' },
  { name:'Student Management System',  icon:'🎓' },
  { name:'CRM Application',            icon:'👥' },
  { name:'LMS Portal',                 icon:'📚' },
  { name:'HRMS Application',           icon:'💼' },
];

const CB_HIGHLIGHTS = [
  { label: 'Industry-Aligned Curriculum',   icon: <MonitorIcon /> },
  { label: 'Hands-on Coding Practice',      icon: <CodeIcon /> },
  { label: 'Mock Interviews & Practice',    icon: <ChatIcon /> },
  { label: 'Capstone Projects',             icon: <TargetIcon /> },
  { label: 'Mentor & Peer Support',         icon: <UsersIcon /> },
  { label: 'Certification & Recognition',   icon: <AwardIcon /> },
  { label: 'Placement Assistance',          icon: <BriefcaseIcon /> },
]

const CB_JOURNEY_STEPS = [
  { label: 'Enrollment & Onboarding', icon: 'enroll',      color: '#7C3AED' },
  { label: 'Foundation Training',     icon: 'book',        color: '#7C3AED' },
  { label: 'Hands-on Practice',       icon: 'training',    color: '#7C3AED' },
  { label: 'Mini Projects',           icon: 'project',     color: '#7C3AED' },
  { label: 'Mock Interviews',         icon: 'interview',   color: '#7C3AED' },
  { label: 'Capstone Project',        icon: 'capstone',    color: '#7C3AED' },
  { label: 'Resume Building',         icon: 'resume',      color: '#7C3AED' },
  { label: 'Certification',           icon: 'certificate', color: '#7C3AED' },
  { label: 'Placement Support',       icon: 'placement',   color: '#7C3AED' },
];

const LEARN_CATEGORIES = [
  {
    title: 'Software Development', accent: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>,
    items: ['Java Full Stack', 'Python Full Stack', 'MERN Stack', 'MEAN Stack', 'ReactJS Development', 'Angular Development', 'NodeJS Development'],
  },
  {
    title: 'Data & AI Programs', accent: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4.03 3-9 3S3 13.66 3 12"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/></svg>,
    items: ['Artificial Intelligence', 'Machine Learning', 'Deep Learning', 'Data Science', 'Generative AI', 'Prompt Engineering'],
  },
  {
    title: 'Cloud & DevOps', accent: '#7C3AED', bg: '#FAF5FF', border: '#DDD6FE',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/></svg>,
    items: ['AWS', 'Azure', 'DevOps', 'Docker', 'Kubernetes'],
  },
  {
    title: 'Emerging Technologies', accent: '#EA580C', bg: '#FFF7ED', border: '#FED7AA',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/></svg>,
    items: ['Blockchain', 'Cyber Security', 'IoT', 'UI/UX', 'Mobile App Development'],
  },
]


const TECH_ICONS = [
  { label: 'Py',   bg: '#3776AB', color: '#FFD43B', top: '8%',  left: '50%' },
  { label: 'Re',   bg: '#20232A', color: '#61DAFB', top: '6%',  left: '66%' },
  { label: 'Java', bg: '#E76F00', color: '#fff',    top: '14%', left: '82%' },
  { label: 'JS',   bg: '#F7DF1E', color: '#000',    top: '62%', left: '48%' },
  { label: '</>',  bg: '#1E3A5F', color: '#60A5FA', top: '44%', left: '41%' },
]

const CODING_BOOTCAMP_TECHS = [
  { name: 'Java',          color: '#E76F00', bg: '#FFF7ED', abbr: '☕', icon: FaJava },
  { name: 'Python',        color: '#3776AB', bg: '#EFF6FF', abbr: '🐍', icon: FaPython },
  { name: 'JavaScript',    color: '#F7DF1E', bg: '#FEFCE8', abbr: 'JS', icon: SiJavascript },
  { name: 'React.js',      color: '#61DAFB', bg: '#F0F9FF', abbr: '⚛', icon: FaReact },
  { name: 'Angular',       color: '#DD0031', bg: '#FEF2F2', abbr: 'Ng', icon: FaAngular },
  { name: 'Node.js',       color: '#339933', bg: '#F0FDF4', abbr: 'N', icon: FaNodeJs },
  { name: 'Express.js',    color: '#000000', bg: '#F8FAFC', abbr: 'Ex', icon: SiExpress },
  { name: 'MongoDB',       color: '#47A248', bg: '#F0FDF4', abbr: 'M', icon: SiMongodb },
  { name: 'MySQL',         color: '#4479A1', bg: '#F0F9FF', abbr: 'SQL', icon: SiMysql },
  { name: 'Machine Learning', color: '#FF6F00', bg: '#FFF7ED', abbr: 'ML', icon: SiTensorflow },
  { name: 'Deep Learning', color: '#15803D', bg: '#F0FDF4', abbr: 'DL', icon: SiTensorflow },
  { name: 'Generative AI', color: '#412991', bg: '#F5F3FF', abbr: 'AI', icon: SiOpenai },
  { name: 'Data Science',  color: '#0D9488', bg: '#F0FDFA', abbr: 'DS', icon: FaMicrochip },
  { name: 'AWS',           color: '#FF9900', bg: '#FFFBEB', abbr: 'aws', icon: FaAws },
  { name: 'Azure',         color: '#0078D4', bg: '#EFF6FF', abbr: 'Az', icon: SiMicrosoftazure },
  { name: 'Docker',        color: '#2496ED', bg: '#EFF6FF', abbr: '🐳', icon: FaDocker },
  { name: 'Kubernetes',    color: '#326CE5', bg: '#EFF6FF', abbr: 'K8s', icon: SiKubernetes },
  { name: 'DevOps',        color: '#7C3AED', bg: '#FAF5FF', abbr: 'Ops' },
  { name: 'Blockchain',    color: '#F16822', bg: '#FFF7ED', abbr: 'BC', icon: SiBlockchaindotcom },
  { name: 'Cyber Security', color: '#EA580C', bg: '#FFF7ED', abbr: '🔒', icon: FaShieldAlt },
  { name: 'IoT',           color: '#EA580C', bg: '#FFF7ED', abbr: 'IoT', icon: FaMicrochip },
  { name: 'UI/UX',         color: '#F24E1E', bg: '#FDF2F8', abbr: 'UX', icon: FaFigma },
  { name: 'Mobile Dev',    color: '#EA580C', bg: '#FFF7ED', abbr: '📱', icon: FaMobileAlt },
  { name: 'Git & GitHub',  color: '#F05032', bg: '#FFF7ED', abbr: '⎇', icon: FaGitAlt },
  { name: 'and more...',   color: '#6B7280', bg: '#F9FAFB', abbr: '...' },
]

const CB_DURATIONS = [
  {
    title: '3 Months Bootcamp', color: '#16A34A', colorDark: '#15803D', bg: '#F0FDF4',
    phases: [
      { name: 'Foundation Training', duration: '1 Month', items: ['Core Programming', 'DSA Basics', 'Git & Tools', 'Problem Solving'] },
      { name: 'Specialization', duration: '1 Month', items: ['Frontend Stack', 'Backend Stack', 'Database', 'APIs'] },
      { name: 'Capstone Project', duration: '1 Month', items: ['Project Build', 'Testing', 'Deployment'] },
    ],
    deliverables: ['Bootcamp Certificate', 'Project Certificate', 'GitHub Portfolio'],
  },
  {
    title: '6 Months Bootcamp', color: '#7C3AED', colorDark: '#6D28D9', bg: '#FAF5FF',
    phases: [
      { name: 'Foundation & Core Tech', duration: '2 Months', items: ['Programming', 'DSA', 'Database', 'Git & Tools'] },
      { name: 'Full Stack Development', duration: '2 Months', items: ['Frontend', 'Backend', 'APIs', 'Cloud Basics'] },
      { name: 'Capstone & Placement Prep', duration: '2 Months', items: ['Live Project', 'Mock Interviews', 'Resume Building'] },
    ],
    deliverables: ['Completion Certificate', 'Capstone Certificate', 'Placement Assistance', 'Industry Portfolio'],
  },
]

const CB_WHY_CHOOSE = [
  { title: 'Industry-Aligned Curriculum', sub: 'Updated with latest tech trends',  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg> },
  { title: 'Learn from Experts',          sub: 'Mentored by industry professionals', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/></svg> },
  { title: 'Hands-on Projects',           sub: 'Build real-world applications',   icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg> },
  { title: 'Placement Support',           sub: 'Resume, interviews & job drives', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
  { title: 'Flexible Learning',           sub: 'Online, hybrid & on-campus',      icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 118 2.83"/><path d="M22 12A10 10 0 0012 2v10z"/></svg> },
  { title: 'Certification & Recognition', sub: 'Stand out and grow faster',       icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg> },
]

export default function CodingBootcamp() {
  const navigate  = useNavigate()
  const dispatch  = useDispatch()
  const programs  = useSelector((s) => s.fetchAllPrograms.programs || [])
  const programImage = useSelector((s) => s.fetchProgramImageById.programImageById)
  const [showApply, setShowApply] = useState(null) // null | 'enroll' | 'download'

  useEffect(() => { dispatch(fetchProgramList()) }, [dispatch])
  useEffect(() => { dispatch(fetchProgramImageById(1)) }, [dispatch])

  const [courses, setCourses] = useState([])
  useEffect(() => {
    axiosinstance.get('/api/category/all/1')
      .then(res => setCourses(res.data.filter(c => c.pdf_file_path)))
      .catch(() => {})
  }, [])

  const handleApplySuccess = async () => {
    if (showApply === 'download') {
      setShowApply(null)
      let pdfFilePath = programImage?.brochure_url
      if (!pdfFilePath) {
        try {
          const res = await axiosinstance.get('/api/programImgAWS/1')
          pdfFilePath = res.data?.brochure_url
        } catch {}
      }
      navigate('/download', {
        state: {
          programInterested:  'Coding Bootcamp',
          Program_id:         1,   // DB: CODING BOOT CAMP
          interestedCourse:   'Coding Bootcamp Program',
          interestedCourseId: 1,   // DB: course_id 1
          pdfFilePath:        pdfFilePath,
          isProgramBrochure:  true,
        }
      })
    }
  }

  return (
    <div style={{ fontFamily:"'Inter','Segoe UI',sans-serif", overflowX:'hidden' }}>
      <SEO
        title="Coding Bootcamp"
        path="/programs/coding-bootcamp"
        description="UPL Snipe's Coding Bootcamp — 4 to 6 month intensive program with Java Full Stack, MERN Stack, Python & more. Hands-on projects, industry mentors, and placement support."
        keywords="coding bootcamp, Java Full Stack course, MERN Stack training, Python bootcamp, web development course, IT bootcamp India"
      />
      <style>{`
        #cb-hero        { background: linear-gradient(135deg,#050D1E 0%,#091525 40%,#0D1F3C 100%) !important; }
        #cb-learn       { background-color: #ffffff !important; }
        #cb-structure   { background-color: #F8FAFF !important; }
        #cb-assessment  { background-color: #ffffff !important; }
        #cb-roles       { background-color: #F8FAFF !important; }
        #cb-methodology { background-color: #ffffff !important; }
        #cb-faq         { background-color: #F8FAFF !important; }
        #cb-cta         { background: linear-gradient(135deg,#0B1A3E 0%,#1E3A8A 100%) !important; }
        #cb-why-choose  { background: #1E1235 !important; }
        @media(max-width:900px){
          #cb-hero { min-height:auto !important; }
          #cb-phase-grid { grid-template-columns:1fr !important; gap:16px !important; }
          #cb-phase-grid .cb-arrow { display:none !important; }
          #cb-learn-grid { grid-template-columns:repeat(2,1fr) !important; }
        }
        @media(max-width:540px){
          #cb-learn-grid { grid-template-columns:1fr !important; }
        }
      `}</style>

      {/* ══ HERO ══ */}
      <section id="cb-hero" style={{ padding:'44px 5% 56px', position:'relative', overflow:'hidden', minHeight:'100vh', boxSizing:'border-box' }}>
        <div style={{ position:'absolute', top:'-10%', right:'10%', width:580, height:580, borderRadius:'50%', background:'radial-gradient(circle,rgba(37,99,235,0.2) 0%,transparent 65%)', pointerEvents:'none' }}/>

        {/* breadcrumb */}
        <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'rgba(255,255,255,0.4)', marginBottom:32, position:'relative', zIndex:2 }}>
          <Link to="/" style={{ color:'rgba(255,255,255,0.4)', textDecoration:'none' }}>Home</Link>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          <Link to="/programs" style={{ color:'rgba(255,255,255,0.4)', textDecoration:'none' }}>Programs</Link>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          <span style={{ color:'rgba(255,255,255,0.75)' }}>Coding Bootcamp</span>
        </div>

        <div style={{ maxWidth:1200, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:48, alignItems:'center', position:'relative', zIndex:2 }}>
          {/* LEFT */}
          <div>
            <h1 style={{ fontSize:44, fontWeight:900, color:'#fff', margin:'0 0 10px', lineHeight:1.05, letterSpacing:'-0.02em' }}>UPL CODING BOOTCAMP</h1>
            <h2 style={{ fontSize:28, fontWeight:800, color:'#60A5FA', margin:'0 0 16px', lineHeight:1.3 }}>
              Transform Learning into<br/>Career Readiness
            </h2>
            <div style={{ display:'inline-flex', alignItems:'center', background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.16)', borderRadius:999, padding:'7px 20px', marginBottom:18 }}>
              {['3 Months','6 Months','Offline','Online','Hybrid'].map((t,i,a) => (
                <React.Fragment key={t}>
                  <span style={{ fontSize:13, fontWeight:700, color:'#fff' }}>{t}</span>
                  {i<a.length-1 && <span style={{ color:'rgba(255,255,255,0.35)', margin:'0 10px' }}>|</span>}
                </React.Fragment>
              ))}
            </div>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.6)', margin:'0 0 28px', lineHeight:1.8, maxWidth:480 }}>
              An intensive 3 to 6 months program to build in-demand IT skills with training, projects, assessments and placement readiness.
            </p>

            <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
              <button onClick={() => setShowApply('enroll')} style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'13px 30px', background:'#2563EB', color:'#fff', borderRadius:8, fontSize:14, fontWeight:700, border:'none', cursor:'pointer', boxShadow:'0 4px 18px rgba(37,99,235,0.45)' }}>
                Enroll Now
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
              <button onClick={() => document.getElementById('courses-section')?.scrollIntoView({ behavior:'smooth' })} style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'13px 28px', background:'transparent', color:'#fff', border:'1.5px solid rgba(255,255,255,0.28)', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
                View Courses
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
            </div>
          </div>

          {/* RIGHT — visual */}
          <div style={{ position:'relative', height:400, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:340, height:340, borderRadius:'50%', background:'radial-gradient(circle,rgba(37,99,235,0.28) 0%,transparent 65%)', pointerEvents:'none' }}/>


            {/* Central icon */}
            <div style={{ width:210, height:210, borderRadius:'50%', background:'rgba(37,99,235,0.12)', border:'2px solid rgba(37,99,235,0.35)', display:'flex', alignItems:'center', justifyContent:'center', position:'relative', zIndex:2 }}>
              <div style={{ width:150, height:150, borderRadius:'50%', background:'rgba(37,99,235,0.18)', border:'1.5px solid rgba(37,99,235,0.4)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
                </svg>
              </div>
            </div>
            <div style={{ position:'absolute', width:290, height:290, borderRadius:'50%', border:'1px dashed rgba(96,165,250,0.18)', pointerEvents:'none' }}/>
          </div>
        </div>
      </section>

      {/* ══ PROGRAM HIGHLIGHTS ══ */}
      <ProgramHighlightsSection
        items={CB_HIGHLIGHTS}
        accentColor="#7C3AED"
        iconBg="#FAF5FF"
        iconBorder="#DDD6FE"
        cardBorder="#EDE9FE"
      />

      {/* ══ COURSES ══ */}
      <div id="courses-section">
        <IncludedCoursesSection courses={courses} programId={1} programName="Coding Bootcamp" />
      </div>

      {/* ══ PROGRAM DURATION ══ */}
      <ProgramDurationSection
        eyebrowColor="#7C3AED" eyebrowBg="#FAF5FF" eyebrowBorder="#DDD6FE"
        titlePlain="Program" titleHighlight="Duration" titleHighlightColor="#7C3AED"
        subtitle="Choose between a focused 3-month bootcamp or an extended 6-month program with placement assistance."
        durations={CB_DURATIONS}
      />

      {/* ══ PROGRAM STRUCTURE ══ */}
      <section id="cb-structure" style={{ padding:'64px 5%' }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
          <h2 style={{ textAlign:'center', fontSize:22, fontWeight:900, color:'#1E3A8A', letterSpacing:'0.06em', margin:'0 0 48px' }}>PROGRAM STRUCTURE</h2>
          <div id="cb-phase-grid" style={{ display:'grid', gridTemplateColumns:'1fr 32px 1fr 32px 1fr 32px 1fr', alignItems:'start' }}>

            {/* Phase 1 */}
            <div style={{ background:'#EFF6FF', border:'1.5px solid #BFDBFE', borderRadius:14, padding:'24px 18px' }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background:'#2563EB', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:18, color:'#fff', margin:'0 auto 12px' }}>1</div>
              <div style={{ textAlign:'center', fontWeight:900, fontSize:13, color:'#1E3A8A', marginBottom:2 }}>PHASE 1</div>
              <div style={{ textAlign:'center', fontWeight:700, fontSize:13, color:'#1E3A8A', marginBottom:14, lineHeight:1.4 }}>Foundation Training<br/>(Month 1)</div>
              {['Programming Fundamentals','Logic Building','Problem Solving','Database Concepts','Git & Version Control','API Integration','Industry Tools'].map(item => (
                <div key={item} style={{ display:'flex', alignItems:'center', gap:7, fontSize:12.5, color:'#374151', marginBottom:7 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {item}
                </div>
              ))}
              <div style={{ marginTop:14, paddingTop:12, borderTop:'1px solid #BFDBFE' }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#2563EB', marginBottom:4 }}>Activities</div>
                <div style={{ fontSize:12, color:'#6B7280', lineHeight:1.6 }}>Daily Coding Practice, Assignments, Weekly Evaluations, Mini Tasks</div>
              </div>
            </div>

            {/* Arrow */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', paddingTop:96 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </div>

            {/* Phase 2 */}
            <div style={{ background:'#F0FDF4', border:'1.5px solid #BBF7D0', borderRadius:14, padding:'24px 18px' }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background:'#16A34A', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:18, color:'#fff', margin:'0 auto 12px' }}>2</div>
              <div style={{ textAlign:'center', fontWeight:900, fontSize:13, color:'#14532D', marginBottom:2 }}>PHASE 2</div>
              <div style={{ textAlign:'center', fontWeight:700, fontSize:13, color:'#14532D', marginBottom:14, lineHeight:1.4 }}>Advanced Training<br/>(Month 2)</div>
              {['Frameworks','APIs','Deployment','SDLC','Testing','Documentation'].map(item => (
                <div key={item} style={{ display:'flex', alignItems:'center', gap:7, fontSize:12.5, color:'#374151', marginBottom:7 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {item}
                </div>
              ))}
              <div style={{ marginTop:14, paddingTop:12, borderTop:'1px solid #BBF7D0' }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#16A34A', marginBottom:4 }}>Activities</div>
                <div style={{ fontSize:12, color:'#6B7280', lineHeight:1.6 }}>Case Studies, Live Coding Sessions, Team Activities, Hackathons</div>
              </div>
            </div>

            {/* Arrow */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', paddingTop:96 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </div>

            {/* Phase 3 */}
            <div style={{ background:'#FAF5FF', border:'1.5px solid #DDD6FE', borderRadius:14, padding:'24px 18px' }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background:'#7C3AED', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:18, color:'#fff', margin:'0 auto 12px' }}>3</div>
              <div style={{ textAlign:'center', fontWeight:900, fontSize:13, color:'#4C1D95', marginBottom:2 }}>PHASE 3</div>
              <div style={{ textAlign:'center', fontWeight:700, fontSize:13, color:'#4C1D95', marginBottom:12, lineHeight:1.4 }}>Capstone Project</div>
              <div style={{ fontSize:12, color:'#6B7280', marginBottom:12, lineHeight:1.6 }}>Build real-world end-to-end projects from scratch.</div>
              <div style={{ fontSize:12, fontWeight:700, color:'#7C3AED', marginBottom:8 }}>Project Examples</div>
              {[{icon:'🎓',text:'Education – LMS'},{icon:'🏥',text:'Healthcare – Hospital System'},{icon:'📚',text:'EdTech – Placement Portal'},{icon:'🤖',text:'AI – Resume Analyzer'},{icon:'💼',text:'Business – CRM System'}].map(p => (
                <div key={p.text} style={{ display:'flex', alignItems:'center', gap:7, fontSize:12, color:'#374151', marginBottom:6 }}>
                  <span>{p.icon}</span>{p.text}
                </div>
              ))}
              <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid #DDD6FE' }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#7C3AED', marginBottom:4 }}>Outputs</div>
                <div style={{ fontSize:12, color:'#6B7280', lineHeight:1.6 }}>Requirement Doc, Design, Development, Testing, Deployment, Presentation</div>
              </div>
            </div>

            {/* Arrow */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', paddingTop:96 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </div>

            {/* Phase 4 */}
            <div style={{ background:'#FFF7ED', border:'1.5px solid #FED7AA', borderRadius:14, padding:'24px 18px' }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background:'#EA580C', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:18, color:'#fff', margin:'0 auto 12px' }}>4</div>
              <div style={{ textAlign:'center', fontWeight:700, fontSize:13, color:'#EA580C', marginBottom:16, lineHeight:1.4 }}>Assessment &amp;<br/>Placement Readiness</div>
              {['Written Test Training','Mock Interviews','Resume Preparation','Career Guidance','Placement Support'].map(item => (
                <div key={item} style={{ display:'flex', alignItems:'center', gap:7, fontSize:12.5, color:'#374151', marginBottom:7 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {item}
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ══ WHAT YOU WILL LEARN ══ */}
      <section id="cb-learn" style={{ padding:'60px 5%' }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
          <h2 style={{ textAlign:'center', fontSize:24, fontWeight:900, color:'#1E3A8A', letterSpacing:'0.04em', margin:'0 0 40px' }}>WHAT YOU WILL LEARN</h2>
          <div id="cb-learn-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:22 }}>
            {LEARN_CATEGORIES.map((cat) => (
              <div key={cat.title} style={{ border:`1.5px solid ${cat.border}`, borderRadius:14, padding:'24px', background:cat.bg }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:18 }}>
                  <div style={{ width:50, height:50, borderRadius:12, background:'#fff', border:`1px solid ${cat.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{cat.icon}</div>
                  <h3 style={{ fontSize:14, fontWeight:800, color:cat.accent, margin:0, lineHeight:1.3 }}>{cat.title}</h3>
                </div>
                <ul style={{ margin:0, padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:9 }}>
                  {cat.items.map((item) => (
                    <li key={item} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#374151' }}>
                      <span style={{ width:6, height:6, borderRadius:'50%', background:cat.accent, flexShrink:0 }}/>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ JOURNEY WORKFLOW ══ */}
      <JourneyWorkflow title="Coding Bootcamp Journey Workflow" steps={CB_JOURNEY_STEPS} accentColor="#7C3AED" />

      {/* ══ TECHNOLOGY YOU WILL LEARN ══ */}
      <TechStackSection title="Technology You Will Learn" accentColor="#7C3AED" bg="#FAF5FF" border="#E2E8F0" techs={CODING_BOOTCAMP_TECHS} />

      {/* ══ LIVE PROJECTS ══ */}
      <LiveProjectsSection
        accentColor="#7C3AED"
        accentBg="#FAF5FF"
        accentBorder="#DDD6FE"
        title="Live Project Internship Experience"
        features={CB_LIVE_FEATURES}
        projects={CB_LIVE_PROJECTS}
      />

      {/* ══ WRITTEN TEST + MOCK INTERVIEW ══ */}
      <section id="cb-assessment" style={{ padding:'64px 5%' }}>
        <div style={{ maxWidth:1200, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:32 }}>

          {/* Written Test Assessment */}
          <div style={{ border:'1.5px solid #E0E7FF', borderRadius:16, padding:'32px 28px' }}>
            <h3 style={{ fontWeight:900, fontSize:16, color:'#1E3A8A', textAlign:'center', margin:'0 0 6px', letterSpacing:'0.03em' }}>WRITTEN TEST ASSESSMENT TRAINING</h3>
            <p style={{ textAlign:'center', fontSize:13, color:'#6B7280', margin:'0 0 24px' }}>We prepare you to crack placement assessments with confidence.</p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              {[
                { title:'Aptitude', color:'#2563EB', icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>, items:['Quantitative Aptitude','Logical Reasoning','Analytical Thinking'] },
                { title:'Coding Assessment', color:'#7C3AED', icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>, items:['Problem Solving','Algorithms','Coding Challenges'] },
                { title:'Soft Skills', color:'#0EA5E9', icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>, items:['Communication','Presentation Skills','Workplace Etiquette'] },
                { title:'Puzzles & Problem Analysis', color:'#EA580C', icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>, items:['Case Analysis','Scenario Solving','Critical Thinking'] },
              ].map(card => (
                <div key={card.title} style={{ border:'1px solid #E0E7FF', borderRadius:10, padding:'16px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                    <div style={{ width:36, height:36, borderRadius:8, background:'#F0F7FF', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{card.icon}</div>
                    <span style={{ fontSize:13, fontWeight:700, color:card.color }}>{card.title}</span>
                  </div>
                  {card.items.map(it => (
                    <div key={it} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#374151', marginBottom:5 }}>
                      <span style={{ width:5, height:5, borderRadius:'50%', background:card.color, flexShrink:0, display:'inline-block' }}/>
                      {it}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Mock Interview Preparation */}
          <div style={{ border:'1.5px solid #E0E7FF', borderRadius:16, padding:'32px 28px' }}>
            <h3 style={{ fontWeight:900, fontSize:16, color:'#1E3A8A', textAlign:'center', margin:'0 0 6px', letterSpacing:'0.03em' }}>MOCK INTERVIEW PREPARATION</h3>
            <p style={{ textAlign:'center', fontSize:13, color:'#6B7280', margin:'0 0 24px' }}>Be interview-ready with expert guidance and practice.</p>
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {[
                { title:'Technical Interviews', icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>, color:'#2563EB', tags:['Programming Questions','Projects Discussion','Coding Tests'] },
                { title:'HR Interviews', icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>, color:'#7C3AED', tags:['Self Introduction','Behavioral Questions','Communication Training'] },
                { title:'Group Discussions', icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>, color:'#EA580C', tags:['Leadership','Teamwork','Presentation'] },
              ].map(row => (
                <div key={row.title} style={{ display:'flex', alignItems:'center', gap:16, border:'1px solid #E0E7FF', borderRadius:10, padding:'16px 18px' }}>
                  <div style={{ width:42, height:42, borderRadius:10, background:'#F0F7FF', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{row.icon}</div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:row.color, marginBottom:6 }}>{row.title}</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                      {row.tags.map((tag,i) => (
                        <span key={tag} style={{ fontSize:12, color:'#6B7280' }}>{i > 0 && <span style={{ marginRight:8, color:'#D1D5DB' }}>•</span>}{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ══ KEY FEATURES + CAREER ROLES + DELIVERABLES ══ */}
      <section id="cb-roles" style={{ padding:'64px 5%' }}>
        <div style={{ maxWidth:1200, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1.6fr 1fr', gap:28 }}>

          {/* Key Features */}
          <div style={{ border:'1.5px solid #E0E7FF', borderRadius:16, padding:'28px 24px' }}>
            <h3 style={{ fontWeight:900, fontSize:15, color:'#1E3A8A', textAlign:'center', margin:'0 0 20px', letterSpacing:'0.04em' }}>KEY FEATURES</h3>
            {['Industry-Focused Curriculum','Hands-on Practical Sessions','Real-world Projects & Assignments','Capstone Project','Written Test Preparation','Mock Interviews','Resume Building Support','Career Guidance','Industry Mentors'].map(f => (
              <div key={f} style={{ display:'flex', alignItems:'center', gap:9, fontSize:13, color:'#374151', marginBottom:10 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                {f}
              </div>
            ))}
          </div>

          {/* Career Roles */}
          <div style={{ border:'1.5px solid #E0E7FF', borderRadius:16, padding:'28px 24px' }}>
            <h3 style={{ fontWeight:900, fontSize:15, color:'#1E3A8A', textAlign:'center', margin:'0 0 20px', letterSpacing:'0.04em' }}>CAREER ROLES YOU CAN TARGET</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[
                {icon:'💻', label:'Software Developer'},{icon:'📊', label:'Data Analyst'},
                {icon:'🖥️', label:'Frontend Developer'},{icon:'☁️', label:'Cloud Associate'},
                {icon:'⚙️', label:'Backend Developer'},{icon:'🎨', label:'UI Developer'},
                {icon:'🔗', label:'Full Stack Developer'},{icon:'🧪', label:'Testing Engineer'},
                {icon:'🤖', label:'AI Engineer'},{icon:'👨‍💻', label:'Junior Developer'},
                {icon:'📈', label:'ML Engineer'},{icon:'➕', label:'and more...'},
              ].map(r => (
                <div key={r.label} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', border:'1px solid #EEF2FF', borderRadius:8, fontSize:12.5, color:'#374151', fontWeight:500 }}>
                  <span style={{ fontSize:16 }}>{r.icon}</span>{r.label}
                </div>
              ))}
            </div>
          </div>

          {/* Deliverables */}
          <div style={{ border:'1.5px solid #E0E7FF', borderRadius:16, padding:'28px 24px' }}>
            <h3 style={{ fontWeight:900, fontSize:15, color:'#1E3A8A', textAlign:'center', margin:'0 0 20px', letterSpacing:'0.04em' }}>DELIVERABLES</h3>
            {[
              {icon:'🏆', label:'Training Completion Certificate'},
              {icon:'📋', label:'Capstone Project Certificate'},
              {icon:'🏅', label:'Internship Recommendation (Eligible Students)'},
              {icon:'👤', label:'Portfolio Support'},
              {icon:'📄', label:'Resume Assistance'},
              {icon:'🐙', label:'GitHub Portfolio Guidance'},
            ].map(d => (
              <div key={d.label} style={{ display:'flex', alignItems:'flex-start', gap:10, fontSize:13, color:'#374151', marginBottom:14 }}>
                <span style={{ fontSize:18, flexShrink:0 }}>{d.icon}</span>
                <span style={{ lineHeight:1.5 }}>{d.label}</span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ══ LEARNING METHODOLOGY ══ */}
      <section id="cb-methodology" style={{ padding:'64px 5%' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', border:'1.5px solid #E0E7FF', borderRadius:18, padding:'44px 48px', background:'#fff' }}>
          <h2 style={{ textAlign:'center', fontSize:22, fontWeight:900, color:'#1E3A8A', letterSpacing:'0.06em', margin:'0 0 48px' }}>LEARNING METHODOLOGY</h2>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'center' }}>
            {[
              { label:'TRAIN', desc:'Learn concepts from experts',
                icon:<svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#1E3A8A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg> },
              { label:'BUILD', desc:'Build projects and applications',
                icon:<svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#1E3A8A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><polyline points="9 9 4 12 9 15"/><polyline points="15 9 20 12 15 15"/><line x1="11" y1="6" x2="13" y2="18"/></svg> },
              { label:'ASSESS', desc:'Evaluate through tests and assignments',
                icon:<svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#1E3A8A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 12l2 2 4-4"/></svg> },
              { label:'INTERVIEW', desc:'Mock interviews and feedback',
                icon:<svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#1E3A8A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg> },
              { label:'DEPLOY', desc:'Get placed & grow your career',
                icon:<svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#1E3A8A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/><line x1="9" y1="15" x2="6" y2="18"/></svg> },
            ].map((step, i, arr) => (
              <React.Fragment key={step.label}>
                <div style={{ textAlign:'center', width:160, flexShrink:0 }}>
                  <div style={{ width:80, height:80, borderRadius:'50%', background:'#EFF6FF', border:'1.5px solid #BFDBFE', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                    {step.icon}
                  </div>
                  <div style={{ fontSize:14, fontWeight:900, color:'#1E3A8A', marginBottom:8, letterSpacing:'0.04em' }}>{step.label}</div>
                  <div style={{ fontSize:12.5, color:'#6B7280', lineHeight:1.6 }}>{step.desc}</div>
                </div>
                {i < arr.length - 1 && (
                  <div style={{ display:'flex', alignItems:'center', paddingBottom:52, flexShrink:0, width:48, justifyContent:'center' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WHY CHOOSE OUR CODING BOOTCAMP ══ */}
      <WhyChooseSection
        id="cb-why-choose"
        title="WHY CHOOSE OUR CODING BOOTCAMP?"
        bg="#1E1235"
        iconColor="#C4B5FD"
        iconBg="rgba(124,58,237,0.15)"
        iconBorder="rgba(196,181,253,0.25)"
        items={CB_WHY_CHOOSE}
      />

      {/* ══ FAQ + COLLEGES ══ */}
      <section id="cb-faq" style={{ padding:'64px 5%' }}>
        <div style={{ maxWidth:1200, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 320px', gap:28, alignItems:'start' }}>

          {/* FAQ */}
          <div>
            <h2 style={{ fontSize:20, fontWeight:900, color:'#1E3A8A', letterSpacing:'0.04em', margin:'0 0 24px' }}>FREQUENTLY ASKED QUESTIONS</h2>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[
                'Who can join the Coding Bootcamp?',
                'Is project work included?',
                'What is the duration of the program?',
                'Is placement preparation included?',
                'What is the training mode?',
                'Will I get a certificate?',
              ].map((q) => (
                <div key={q} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', border:'1.5px solid #E0E7FF', borderRadius:10, padding:'16px 18px', background:'#fff', cursor:'pointer', gap:12 }}>
                  <span style={{ fontSize:13.5, color:'#374151', fontWeight:500, lineHeight:1.4 }}>{q}</span>
                  <span style={{ width:24, height:24, borderRadius:'50%', border:'1.5px solid #BFDBFE', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:'#2563EB', fontSize:18, fontWeight:300 }}>+</span>
                </div>
              ))}
            </div>
          </div>

          {/* Colleges */}
          <div style={{ border:'1.5px solid #E0E7FF', borderRadius:16, padding:'32px 28px', background:'#fff', textAlign:'center' }}>
            <div style={{ width:72, height:72, borderRadius:'50%', background:'#EFF6FF', border:'1.5px solid #BFDBFE', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                <line x1="12" y1="2" x2="12" y2="5"/><rect x="7" y="14" width="3" height="3"/><rect x="14" y="14" width="3" height="3"/>
              </svg>
            </div>
            <h3 style={{ fontSize:16, fontWeight:900, color:'#1E3A8A', margin:'0 0 12px', letterSpacing:'0.03em' }}>COLLEGES / INSTITUTIONS</h3>
            <p style={{ fontSize:13, color:'#6B7280', lineHeight:1.7, margin:'0 0 24px' }}>
              We invite colleges to partner with us for training, assessments, and placement readiness programs.
            </p>
            <Link to="/contact" style={{ display:'inline-block', padding:'12px 32px', background:'#2563EB', color:'#fff', borderRadius:8, fontSize:14, fontWeight:700, textDecoration:'none', boxShadow:'0 4px 14px rgba(37,99,235,0.35)' }}>
              Send Enquiry
            </Link>
          </div>

        </div>
      </section>

      {/* ══ CTA BANNER ══ */}
      <section id="cb-cta" style={{ padding:'56px 5%' }}>
        <div style={{ maxWidth:1200, margin:'0 auto', display:'flex', alignItems:'center', gap:40 }}>
          {/* Robot illustration */}
          <div style={{ flexShrink:0, width:130, height:130, position:'relative' }}>
            <div style={{ width:130, height:130, borderRadius:'50%', background:'rgba(255,255,255,0.07)', border:'1.5px solid rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="72" height="72" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="20" r="14" stroke="#60A5FA" strokeWidth="2" fill="rgba(37,99,235,0.2)"/>
                <rect x="18" y="14" width="28" height="20" rx="6" fill="rgba(37,99,235,0.3)" stroke="#60A5FA" strokeWidth="1.5"/>
                <circle cx="25" cy="22" r="3" fill="#60A5FA"/>
                <circle cx="39" cy="22" r="3" fill="#60A5FA"/>
                <path d="M25 29 Q32 34 39 29" stroke="#60A5FA" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                <rect x="22" y="34" width="20" height="18" rx="4" fill="rgba(37,99,235,0.25)" stroke="#60A5FA" strokeWidth="1.5"/>
                <line x1="32" y1="34" x2="32" y2="52" stroke="#60A5FA" strokeWidth="1"/>
                <line x1="16" y1="36" x2="22" y2="40" stroke="#60A5FA" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="48" y1="36" x2="42" y2="40" stroke="#60A5FA" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="25" y1="52" x2="23" y2="60" stroke="#60A5FA" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="39" y1="52" x2="41" y2="60" stroke="#60A5FA" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
          </div>

          {/* Text */}
          <div style={{ flex:1 }}>
            <h2 style={{ fontSize:28, fontWeight:900, color:'#fff', margin:'0 0 10px', lineHeight:1.2 }}>Ready to launch your tech career?</h2>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.65)', margin:0, lineHeight:1.7 }}>
              Join UPL Coding Bootcamp and become industry ready through training, projects and placement preparation.
            </p>
          </div>

          {/* Buttons */}
          <div style={{ display:'flex', gap:16, flexShrink:0 }}>
            <button onClick={() => setShowApply('enroll')} style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'14px 32px', background:'#2563EB', color:'#fff', borderRadius:8, fontSize:15, fontWeight:700, border:'none', cursor:'pointer', boxShadow:'0 4px 18px rgba(37,99,235,0.45)', whiteSpace:'nowrap' }}>
              Apply Now
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
            <Link to="/contact" style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'14px 28px', background:'transparent', color:'#fff', border:'1.5px solid rgba(255,255,255,0.35)', borderRadius:8, fontSize:15, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap' }}>
              Talk to an Advisor
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013 5.18a2 2 0 012-2.18h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L9.91 9.91a16 16 0 006.18 6.18l1.27-1.34a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
            </Link>
          </div>
        </div>
      </section>

      {showApply && (
        <RequestCallback
          isModal
          onClose={() => setShowApply(null)}
          onSuccess={handleApplySuccess}
          defaultProgram="Coding Bootcamp"
        />
      )}
    </div>
  )
}
